/**
 * Hook for fetching RentCast market data
 */
import { useState, useEffect, useCallback } from 'react';
import { api } from '../api/client';
import type {
  MarketDataStatesResponse,
  MarketDataStateResponse,
  MarketDataMarketResponse,
  MarketDataZip,
  MarketDataRefreshResponse,
} from '../api/client';

interface UseMarketDataStatesResult {
  data: MarketDataStatesResponse | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

interface UseMarketDataStateResult {
  data: MarketDataStateResponse | null;
  loading: boolean;
  error: string | null;
}

interface UseMarketDataMarketResult {
  data: MarketDataMarketResponse | null;
  loading: boolean;
  error: string | null;
}

interface UseMarketDataZipResult {
  data: MarketDataZip | null;
  loading: boolean;
  error: string | null;
}

interface UseMarketDataRefreshResult {
  refresh: (params: { market?: string; zips?: string[] }) => Promise<MarketDataRefreshResponse>;
  loading: boolean;
  error: string | null;
}

/**
 * Hook to fetch market data states summary
 */
export function useMarketDataStates(): UseMarketDataStatesResult {
  const [data, setData] = useState<MarketDataStatesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.getMarketDataStates();
      setData(result);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to fetch market data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refresh: fetchData };
}

/**
 * Hook to fetch market data for a specific state
 */
export function useMarketDataByState(stateCode: string | null): UseMarketDataStateResult {
  const [data, setData] = useState<MarketDataStateResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!stateCode) {
      setData(null);
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await api.getMarketDataByState(stateCode);
        setData(result);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to fetch state data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [stateCode]);

  return { data, loading, error };
}

/**
 * Hook to fetch market data for a specific market (with ZIP breakdown)
 */
export function useMarketDataByMarket(marketSlug: string | null): UseMarketDataMarketResult {
  const [data, setData] = useState<MarketDataMarketResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!marketSlug) {
      setData(null);
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await api.getMarketDataByMarket(marketSlug);
        setData(result);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to fetch market data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [marketSlug]);

  return { data, loading, error };
}

/**
 * Hook to fetch market data for a specific ZIP code
 */
export function useMarketDataByZip(zipCode: string | null): UseMarketDataZipResult {
  const [data, setData] = useState<MarketDataZip | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!zipCode) {
      setData(null);
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await api.getMarketDataByZip(zipCode);
        setData(result);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to fetch ZIP data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [zipCode]);

  return { data, loading, error };
}

/**
 * Hook for refreshing market data from RentCast API
 */
export function useMarketDataRefresh(): UseMarketDataRefreshResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async (params: { market?: string; zips?: string[] }) => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.refreshMarketData(params);
      return result;
    } catch (e) {
      const errorMsg = e instanceof Error ? e.message : 'Failed to refresh market data';
      setError(errorMsg);
      throw e;
    } finally {
      setLoading(false);
    }
  }, []);

  return { refresh, loading, error };
}

/**
 * Format rent value for display
 */
export function formatRent(value?: number | string | null): string {
  if (value === null || value === undefined) return '-';
  const numValue = Number(value);
  if (isNaN(numValue) || numValue === 0) return '-';
  return `$${numValue.toLocaleString()}`;
}

/**
 * Format rent growth percentage for display
 */
export function formatRentGrowth(value?: number | string | null): string {
  if (value === null || value === undefined) return '-';
  const numValue = Number(value);
  if (isNaN(numValue)) return '-';
  const sign = numValue >= 0 ? '+' : '';
  return `${sign}${numValue.toFixed(1)}%`;
}

/**
 * Get CSS class for rent growth (positive = green, negative = red)
 */
export function getRentGrowthClass(value?: number | string | null): string {
  if (value === null || value === undefined) return '';
  const numValue = Number(value);
  if (isNaN(numValue)) return '';
  return numValue >= 0 ? 'value-positive' : 'value-negative';
}

/**
 * Format days on market
 */
export function formatDaysOnMarket(value?: number | string | null): string {
  if (value === null || value === undefined) return '-';
  const numValue = Number(value);
  if (isNaN(numValue)) return '-';
  return `${Math.round(numValue)}d`;
}

/**
 * Format listing count
 */
export function formatListings(value?: number | string | null): string {
  if (value === null || value === undefined) return '-';
  const numValue = Number(value);
  if (isNaN(numValue) || numValue === 0) return '-';
  return numValue.toLocaleString();
}

/**
 * Format freshness date
 */
export function formatFreshness(dateStr?: string | null): string {
  if (!dateStr) return 'Never';
  const date = new Date(dateStr);
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return '1 day ago';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
  return `${Math.floor(diffDays / 365)} years ago`;
}
